import { Controller } from '@hotwired/stimulus';
import { trackShown } from '../lib/funnel';

/**
 * Sitewide popup offering the FREE Big Year calendar.
 * The server decides WHO and WHERE (BigYearPromoHelper#show_big_year_popup?),
 * so this controller only decides WHEN: the first of
 *   - the visitor scrolls past 50% of the page (all devices)
 *   - desktop exit intent: the mouse leaves through the top of the viewport
 *     (only on devices with a fine, hovering pointer; phones get scroll only)
 * It opens at most once per browser every SEEN_TTL_DAYS (localStorage).
 * LEGACY_SEEN_KEYS are still read, so people who saw the homepage-only
 * version count as seen.
 * upsell_shown goes to funnel_events when it opens, via trackShown().
 */
export default class extends Controller {
  static targets = ['dialog', 'offer'];

  static SEEN_KEY = 'bs_big_year_popup_seen_at';
  static LEGACY_SEEN_KEYS = ['bs_home_big_year_popup_seen_at'];
  static SEEN_TTL_DAYS = 14;
  static SCROLL_THRESHOLD = 0.5;

  connect() {
    if (this.recentlySeen()) return;

    this.onScroll = this.onScroll.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);

    window.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.hasExitIntent()) document.addEventListener('mouseout', this.onMouseOut);
  }

  disconnect() {
    this.removeTriggers();
  }

  onScroll() {
    const doc = document.documentElement;
    const scrolledTo = window.scrollY + window.innerHeight;
    if (doc.scrollHeight > 0 && scrolledTo / doc.scrollHeight >= this.constructor.SCROLL_THRESHOLD) this.open();
  }

  onMouseOut(event) {
    // Leaving the document (no relatedTarget) across the top edge = heading for tabs/URL bar.
    if (!event.relatedTarget && event.clientY <= 0) this.open();
  }

  open() {
    this.removeTriggers();
    if (!this.hasDialogTarget || this.dialogTarget.open || this.recentlySeen()) return;

    this.dialogTarget.showModal();
    this.markSeen();
    if (this.hasOfferTarget) trackShown(this.offerTarget);
  }

  close() {
    if (this.hasDialogTarget && this.dialogTarget.open) this.dialogTarget.close();
  }

  backdropClose(event) {
    if (event.target === this.dialogTarget) this.close();
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  // Keep an open dialog out of Turbo's page snapshot (Back would restore it half-open).
  dismissForCache() {
    this.close();
  }

  removeTriggers() {
    if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    if (this.onMouseOut) document.removeEventListener('mouseout', this.onMouseOut);
  }

  hasExitIntent() {
    try {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    } catch (e) {
      return false;
    }
  }

  recentlySeen() {
    try {
      const ttl = this.constructor.SEEN_TTL_DAYS * 24 * 60 * 60 * 1000;
      const keys = [this.constructor.SEEN_KEY, ...this.constructor.LEGACY_SEEN_KEYS];
      return keys.some((key) => {
        const seenAt = Number(window.localStorage.getItem(key));
        return seenAt > 0 && Date.now() - seenAt < ttl;
      });
    } catch (e) {
      return false;
    }
  }

  markSeen() {
    try {
      window.localStorage.setItem(this.constructor.SEEN_KEY, String(Date.now()));
    } catch (e) {
      // localStorage unavailable (private mode, etc.): it may show again on a later visit.
    }
  }
}
